import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../lib/utils"
import { Loader2 } from "lucide-react"

/**
 * Design System v3.0 — Light/Flat Buttons
 *
 * Primary  → solid indigo fill (flat, no gradient/glow), hairline darken on hover
 * Secondary→ white fill + 1px hairline border, faint fill on hover
 * Ghost    → transparent, indigo label, tinted hover
 * FAB      → solid indigo square, radius 14px, no scale/glow
 *
 * No shadows, no glassmorphism, no lift. Separation comes from borders + ink.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold",
    "transition-colors duration-fast ease-default outline-none",
    "focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:translate-y-px",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        /* Primary action = solid blue (one per view) */
        default:
          "bg-primary text-primary-foreground border border-primary hover:bg-primary/90 hover:border-primary/90",
        primary:
          "bg-primary text-primary-foreground border border-primary hover:bg-primary/90 hover:border-primary/90",
        /* Semantic actions = solid flat fills */
        destructive:
          "bg-destructive text-destructive-foreground border border-destructive hover:bg-destructive/90",
        danger:
          "bg-destructive text-destructive-foreground border border-destructive hover:bg-destructive/90",
        error:
          "bg-destructive text-destructive-foreground border border-destructive hover:bg-destructive/90",
        success:
          "bg-success text-success-foreground border border-success hover:bg-success/90",
        warning:
          "bg-warning text-warning-foreground border border-warning hover:bg-warning/90",
        info: "bg-info text-info-foreground border border-info hover:bg-info/90",
        /* Secondary = white fill + hairline border */
        secondary:
          "bg-card text-foreground border border-border hover:bg-surface-2",
        outline:
          "bg-transparent text-foreground border border-border hover:bg-surface-2",
        /* Ghost = neutral, for icon/toolbar/table-row actions */
        ghost:
          "bg-transparent text-muted-foreground border border-transparent hover:bg-secondary hover:text-foreground",
        tertiary:
          "bg-transparent text-primary border border-transparent hover:bg-accent",
        link: "bg-transparent text-primary underline-offset-4 hover:underline border border-transparent",
        /* FAB — solid blue square */
        fab: "bg-primary text-primary-foreground border border-primary hover:bg-primary/90",
        /* Passthrough: no colors — element keeps its own className. */
        unstyled: "",
      },
      size: {
        /* Enterprise density: 36px default, 14px text, 8px radius */
        default: "h-9 px-4 text-sm rounded-lg",
        md: "h-9 px-4 text-sm rounded-lg",
        sm: "h-8 px-3 text-sm rounded-md",
        xs: "h-7 px-2.5 text-xs rounded-md [&_svg]:size-3.5",
        lg: "h-10 px-5 text-sm rounded-lg gap-2",
        xl: "h-11 px-6 text-base rounded-lg gap-2.5",
        /* Icon buttons */
        icon: "size-9 rounded-lg [&_svg]:size-4",
        "icon-sm": "size-8 rounded-md [&_svg]:size-4",
        "icon-lg": "size-10 rounded-lg [&_svg]:size-5",
        /* FAB — 48x48 */
        fab: "size-12 rounded-lg [&_svg]:size-5",
        /* Passthrough size — no forced height/padding. */
        unstyled: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  fullWidth?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, isLoading, fullWidth, leftIcon, rightIcon, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), fullWidth && "w-full")}
        ref={ref}
        type={asChild ? undefined : (props.type ?? "button")}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading && <Loader2 className="size-4 animate-spin" />}
        {!isLoading && leftIcon}
        {children}
        {!isLoading && rightIcon}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
